import {
  NotificationDisplayText,
  NotificationDto,
  OVERDUE_NOTIFICATION_SUBTYPES,
  resolveNotificationIconBackground
} from "@/api/dto/notification";
import {SmallTypography, StandardSemiBoldTypography} from "patient-ping-remedy/packages/typography";
import {NotificationIcons} from "@/components/pages/notifications/NotificationIcon";
import {colors} from "patient-ping-remedy/packages/theme";
import Spacer from "patient-ping-remedy/packages/spacer";
import React, {useRef, useState, useEffect, useCallback} from "react";
import propsAreEqual from "react-fast-compare";
import {
  Wrapper,
  IconWithBackground,
  NotificationElement,
  TimePassedComponent, DescriptionRow, NotificationDescription, IconWrapper, ViewMoreButton
} from "@/components/pages/notifications/popover/NotificationPopoverItem.styles";
import WidgetElementViewed from "@common/widgets/WidgetElementViewed";
import NotificationContent from "@/components/pages/notifications/NotificationContent";
import {useNotificationItem} from "@/hooks/useNotificationItem";
import {useTimeSince} from "@/hooks/useTimeSince";
import DisplayHelpers from "@/helpers/display_helpers";
import {GroupType, Roster} from "@/api/dto/dto";

type Props = {
  notification: NotificationDto,
  roster?: Roster;
}

function NotificationPopoverItem({notification, roster}: Props) {
  const {setNotificationViewed, isApiLoading} = useNotificationItem();
  const timePassed = useTimeSince(notification, 'notificationDate', true);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const checkTruncation = useCallback(() => {
    const el = descriptionRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [checkTruncation]);

  // Check if today's date matches the notification date
  const notificationDate = new Date(notification.notificationDate);
  const today = new Date();
  const clientRoster = notification?.client?.roster;
  const isSameDate =
    notificationDate.getFullYear() === today.getFullYear() &&
    notificationDate.getMonth() === today.getMonth() &&
    notificationDate.getDate() === today.getDate();
  const isManualEvent = (clientRoster?.admittingFacility &&
    !clientRoster.automated &&
    clientRoster.type !== GroupType.CRISIS);
  const displayTime = isManualEvent && isSameDate 
    ? DisplayHelpers.formatDate(notification.notificationDate) 
    : timePassed;

  const notificationDisplayTextMap = NotificationDisplayText[
    notification.notificationType];
  if (!notificationDisplayTextMap) {
    return null;
  }
  const eventAndCategoryText = notificationDisplayTextMap[notification.notificationSubtype]
    ? notificationDisplayTextMap[notification.notificationSubtype](notification)
    : notification.notificationSubtype;

  return (
    <Wrapper>
      <IconWrapper>
        <IconWithBackground
          backgroundColor={resolveNotificationIconBackground(notification.notificationType)}
        >
          {NotificationIcons[notification.notificationType](notification)}
        </IconWithBackground>
      </IconWrapper>
      <NotificationElement>
        <StandardSemiBoldTypography color={colors.primary1}>
          {eventAndCategoryText} <Spacer itemWidth={'5px'}/>
        </StandardSemiBoldTypography>
        <Spacer itemHeight={'5px'}/>
        <DescriptionRow>
          <NotificationDescription ref={descriptionRef} expanded={expanded}>
            <NotificationContent roster={roster} notification={notification}/>
            <Spacer itemWidth={'10px'}/>
          </NotificationDescription>
          {(isTruncated || expanded) && (
            <ViewMoreButton collapsed={!expanded} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'View less' : 'View more'}
            </ViewMoreButton>
          )}
        </DescriptionRow>
      </NotificationElement>
      <TimePassedComponent>
        <SmallTypography>
          {OVERDUE_NOTIFICATION_SUBTYPES.includes(notification.notificationSubtype) && (
            <span style={{ color: '#B71C1C', fontWeight: 'bold' }}>(Overdue) </span>
          )}
          {displayTime}
        </SmallTypography>
      </TimePassedComponent>
      <WidgetElementViewed
        viewed={notification.viewed}
        disabled={isApiLoading}
        concise
        onClick={() => setNotificationViewed(notification.id, !notification.viewed)}
        data-testid="mark-notification-as-viewed"
      />
    </Wrapper>
  );
}

export default React.memo(NotificationPopoverItem, propsAreEqual);
